if (!customElements.get('slideshow-component')) {
    customElements.define('slideshow-component', class SlideshowComponent extends HTMLElement {
        constructor() {
            super();
            this.slideshow = this.querySelector('.slideshow');
        }

        connectedCallback() {
            const autoplayEnabled = this.getAttribute('data-autoplay') === 'true';
            const autoPlaySpeed = parseInt(this.getAttribute('data-speed'), 10) * 1000;
            this.autoPlayEnabled = autoplayEnabled && autoPlaySpeed > 0;

            if (!this.slideshow) return;

            // 1. Gather all slide elements to keep in memory
            this.allSlides = Array.from(this.querySelectorAll('.js-scheduled-slide'));
            
            // 2. Hide invalid slides immediately so Flickity doesn't initialize with them
            const now = Math.floor(Date.now() / 1000);
            this.allSlides.forEach(slide => {
                if (!this.evaluateVisibility(slide, now)) {
                    slide.remove(); // Take out of DOM (but safe in this.allSlides)
                } else {
                    slide.style.display = ''; // Ensure it's visible
                }
            });

            this.slideshow.style.display = 'block';
            
            // 3. Initialize Flickity ONLY with the active slides
            this.flickity = new Flickity(this.slideshow, {
                autoPlay: this.autoPlayEnabled ? autoPlaySpeed : false,
                pauseAutoPlayOnHover: false,
                cellAlign: 'left',
                percentPosition: true,
                fullscreen: true,
                contain: true,
                resize: true,
                draggable: true,
                prevNextButtons: false,
                fade: false,
                cellSelector: '.slideshow-slide',
                initialIndex: 0,
                pageDots: false,
                wrapAround: true,
                accessibility: false,
            });

            if (this.autoPlayEnabled && this.flickity.cells.length > 0) {
                this.flickity.playPlayer();
            }

            this.flickity.on('change', (index) => {
                const event = new CustomEvent('slideshow:slide-change', {
                    detail: { id: this.getAttribute('id'), index },
                });
                document.dispatchEvent(event);
            });

            // START HEARTBEAT
            this.initScheduler();
        }

        evaluateVisibility(slide, now) {
            if (slide.dataset.enabled !== 'true') return true;
            const start = parseInt(slide.dataset.start);
            const end = parseInt(slide.dataset.end);
            const type = slide.dataset.type;
            const inRange = (now >= start && now <= end);
            return type === 'enable' ? inRange : !inRange;
        }

        initScheduler() {
            this.schedulerInterval = setInterval(() => {
                if (!this.flickity) return;
                const now = Math.floor(Date.now() / 1000);
                let hasChanges = false;

                // Check every single slide in memory
                this.allSlides.forEach((slide) => {
                    const shouldBeVisible = this.evaluateVisibility(slide, now);
                    const isCurrentlyVisible = this.flickity.cells.some(c => c.element === slide);

                    if (shouldBeVisible && !isCurrentlyVisible) {
                        // SWAP IN: Add slide to slider live
                        slide.style.display = ''; 
                        this.flickity.append(slide);
                        hasChanges = true;
                    } 
                    else if (!shouldBeVisible && isCurrentlyVisible) {
                        // SWAP OUT: Remove slide from slider live
                        const cellIndex = this.flickity.cells.findIndex(c => c.element === slide);
                        if (this.flickity.selectedIndex === cellIndex) {
                            this.flickity.next(true, true); // Snap to next before removing to prevent glitch
                        }
                        this.flickity.remove(slide);
                        hasChanges = true;
                    }
                });

                if (hasChanges) {
                    this.flickity.resize();
                    
                    // Toggle section visibility and drag behavior
                    if (this.flickity.cells.length === 0) {
                        this.style.display = 'none';
                    } else {
                        this.style.display = 'block';
                        this.flickity.options.draggable = this.flickity.cells.length > 1;
                        this.flickity.updateDraggable();
                        if (this.autoPlayEnabled) this.flickity.playPlayer();
                    }

                    // Tell the Navigation to redraw the dots
                    const nav = document.querySelector(`slideshow-navigation[for="${this.getAttribute('id')}"]`);
                    if (nav) nav.updateButtons();
                    
                    window.dispatchEvent(new Event('resize'));
                }
            }, 1000);
        }

        disconnectedCallback() {
            if (this.schedulerInterval) clearInterval(this.schedulerInterval);
        }
    });
}

if (!customElements.get('slideshow-navigation')) {
    customElements.define('slideshow-navigation', class SlideshowNavigation extends HTMLElement {
        constructor() {
            super();
            this.slideshow = this.closest('slideshow-component');
        }

        connectedCallback() {
            if (!this.slideshow || !this.slideshow.flickity) {
                setTimeout(() => this.connectedCallback(), 50);
                return;
            }

            this.flickity = this.slideshow.flickity;
            this.paginationContainer = this.querySelector('.slideshow-pagination');
            
            this.updateButtons();

            this.flickity.on('select', () => this.updateButtons());

            this.buttonPrev = this.querySelector('.js-prev');
            this.buttonNext = this.querySelector('.js-next');

            if (this.buttonPrev) {
                this.buttonPrev.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.flickity.previous();
                    if (this.slideshow.autoPlayEnabled) this.flickity.playPlayer();
                });
            }

            if (this.buttonNext) {
                this.buttonNext.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.flickity.next();
                    if (this.slideshow.autoPlayEnabled) this.flickity.playPlayer();
                });
            }
        }

        updateButtons() {
            if (!this.paginationContainer) return;

            const activeCellsCount = this.flickity.cells.length;
            let buttons = Array.from(this.querySelectorAll('.js-page'));

            // CREATE dots dynamically if a slide was swapped in
            while (buttons.length < activeCellsCount) {
                const btn = document.createElement('button');
                btn.className = 'slideshow-pagination-link js-page';
                btn.innerHTML = '<span class="slideshow-dot"></span>';
                this.paginationContainer.appendChild(btn);
                buttons.push(btn);
            }

            // Show/Hide buttons based on active cells
            buttons.forEach((button, index) => {
                if (index >= activeCellsCount) {
                    button.style.display = 'none'; // Hide if slide was swapped out
                } else {
                    button.style.display = '';
                    button.classList.remove('is-active');
                    if (index === this.flickity.selectedIndex) {
                        button.classList.add('is-active');
                    }
                    
                    if (!button.dataset.initialized) {
                        button.addEventListener('click', () => {
                            this.flickity.select(index);
                            if (this.slideshow.autoPlayEnabled) this.flickity.playPlayer();
                        });
                        button.dataset.initialized = 'true';
                    }
                }
            });

            // Hide arrows/dots completely if only 1 slide exists
            if (activeCellsCount <= 1) {
                this.style.display = 'none';
            } else {
                this.style.display = 'block';
            }
        }
    });
}